import { useEffect, useRef } from 'react';
import { pingSession } from 'services/sessionManagerService';

/** Minimum idle time (ms) between keepalive pings. */
const KEEPALIVE_DEBOUNCE_MS = 30_000;

/**
 * Keeps the BAO session alive while the user is actively filling in the form.
 *
 * Listens for `keydown` and `click` events on the document and fires a
 * session-manager ping at most once every KEEPALIVE_DEBOUNCE_MS (30 s).
 * This ensures the Redis TTL is renewed before the 30-minute session window
 * elapses during a long form interaction.
 *
 * The hook is a no-op when `requestCode` is null/undefined (i.e., before the
 * user has an active BAO session).
 */
const useSessionKeepalive = (requestCode: string | null | undefined): void => {
  const lastPingRef = useRef<number>(0);

  useEffect(() => {
    if (!requestCode) return;

    const handleActivity = (): void => {
      const now = Date.now();
      if (now - lastPingRef.current >= KEEPALIVE_DEBOUNCE_MS) {
        lastPingRef.current = now;
        pingSession(requestCode);
      }
    };

    document.addEventListener('keydown', handleActivity);
    document.addEventListener('click', handleActivity);

    return () => {
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('click', handleActivity);
    };
  }, [requestCode]);
};

export default useSessionKeepalive;
